import type { CourseTreeNode } from "@/types/generated";
import type { QuizStoryboard } from "@/types/storyboard";
import { QuizValidationService } from "@/services/QuizValidationService";
import { StoryboardFallback } from "@/components/storyboard/StoryboardFallback";
import QuizTrueFalseFlow from "@/components/storyboard/quiz/QuizTrueFalseFlow";
import QuizGapwordDropdownFlow from "@/components/storyboard/quiz/QuizGapwordDropdownFlow";
import QuizGapwordFreeTextFlow from "@/components/storyboard/quiz/QuizGapwordFreeTextFlow";
import QuizMultipleChoiceFlow from "@/components/storyboard/quiz/QuizMultipleChoiceFlow";
import QuizFreetextFlow from "@/components/storyboard/quiz/QuizFreetextFlow";
import QuizMatchingFlow from "@/components/storyboard/quiz/QuizMatchingFlow";
import QuizCodeTrueFalseFlow from "@/components/storyboard/quiz/QuizCodeTrueFalseFlow";
import QuizCodeFreetextFlow from "@/components/storyboard/quiz/QuizCodeFreetextFlow";
import QuizCodeDebugFlow from "@/components/storyboard/quiz/QuizCodeDebugFlow";
import QuizCodeAssertionFlow from "@/components/storyboard/quiz/QuizCodeAssertionFlow";
import QuizCodeExplanationFlow from "@/components/storyboard/quiz/QuizCodeExplanationFlow";
import { encodeClozeFreetextAnswers } from "@/lib/freetextQuizStatus";
import { illustrationKindFromSubType } from "@/lib/interactiveIllustration";
import { storyboardTitle } from "@/lib/storyboardTitle";

interface Props {
  data: QuizStoryboard;
  contentElement: CourseTreeNode;
}

export const StoryboardQuizRenderer = ({ data, contentElement }: Props) => {
  const cardTitle = storyboardTitle(data.title, contentElement.title);

  switch (data.subType) {
    case "FREETEXT":
      return (
        <QuizFreetextFlow
          id={contentElement.id}
          title={cardTitle}
          data={data}
          onValidate={(userAnswer) =>
            QuizValidationService.validateAnswer({
              type: "freetext",
              userInput: userAnswer,
              contentElementId: contentElement.id,
              entryIndex: 0,
            })
          }
        />
      );
    case "TRUE_FALSE":
      return (
        <QuizTrueFalseFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={async (answer, entryIndex) => {
            const result = await QuizValidationService.validateAnswer({
              type: "true_false",
              userInput: String(answer),
              contentElementId: contentElement.id,
              entryIndex,
            });
            return result.correct === true;
          }}
        />
      );
    case "MULTIPLE_CHOICE":
    case "CODE_MULTIPLE_CHOICE":
      return (
        <QuizMultipleChoiceFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          illustrationKind={illustrationKindFromSubType(data.subType)}
          variant={data.subType === "CODE_MULTIPLE_CHOICE" ? "code" : "default"}
          onValidate={(selectedLabels, entryIndex) =>
            QuizValidationService.validateAnswer({
              type:
                data.subType === "CODE_MULTIPLE_CHOICE"
                  ? "code_multiple_choice"
                  : "multiple_choice",
              userInput: JSON.stringify(selectedLabels),
              contentElementId: contentElement.id,
              entryIndex,
            })
          }
        />
      );
    case "CLOZE_FREETEXT":
      return (
        <QuizGapwordFreeTextFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={(answers, entryIndex) =>
            QuizValidationService.validateAnswer({
              type: "cloze_freetext",
              userInput: encodeClozeFreetextAnswers(answers),
              contentElementId: contentElement.id,
              entryIndex,
            })
          }
        />
      );
    case "CLOZE_DROPDOWN":
      return (
        <QuizGapwordDropdownFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={async (userAnswers, entryIndex) => {
            const result = await QuizValidationService.validateAnswer({
              type: "cloze_dropdown",
              userInput: JSON.stringify(userAnswers),
              contentElementId: contentElement.id,
              entryIndex,
            });
            return result.correct === true;
          }}
        />
      );
    case "MATCHING_DEFINITION":
    case "MATCHING_SENTENCE":
      return (
        <QuizMatchingFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          illustrationKind={illustrationKindFromSubType(data.subType)}
          onValidate={async (userPairs, entryIndex) => {
            const result = await QuizValidationService.validateAnswer({
              type:
                data.subType === "MATCHING_SENTENCE"
                  ? "matching_sentence"
                  : "matching_definition",
              userInput: JSON.stringify(userPairs),
              contentElementId: contentElement.id,
              entryIndex,
            });
            return result.correct === true;
          }}
        />
      );
    case "CODE_TRUE_FALSE":
      return (
        <QuizCodeTrueFalseFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={async (answer, entryIndex) => {
            const result = await QuizValidationService.validateAnswer({
              type: "code_true_false",
              userInput: String(answer),
              contentElementId: contentElement.id,
              entryIndex,
            });
            return result.correct === true;
          }}
        />
      );
    case "CODE_FREETEXT":
      return (
        <QuizCodeFreetextFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={(userCode, entryIndex) =>
            QuizValidationService.validateAnswer({
              type: "code_freetext",
              userInput: userCode,
              contentElementId: contentElement.id,
              entryIndex,
            })
          }
        />
      );
    case "CODE_DEBUG":
      return (
        <QuizCodeDebugFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={(userCode, entryIndex) =>
            QuizValidationService.validateAnswer({
              type: "code_debug",
              userInput: userCode,
              contentElementId: contentElement.id,
              entryIndex,
            })
          }
        />
      );
    case "CODE_ASSERTION":
      return (
        <QuizCodeAssertionFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={(userCode, entryIndex) =>
            QuizValidationService.validateAnswer({
              type: "code_assertion",
              userInput: userCode,
              contentElementId: contentElement.id,
              entryIndex,
            })
          }
        />
      );
    case "CODE_EXPLANATION":
      return (
        <QuizCodeExplanationFlow
          id={contentElement.id}
          title={cardTitle}
          entries={data.entries}
          onValidate={(explanation, entryIndex) =>
            QuizValidationService.validateAnswer({
              type: "code_explanation",
              userInput: explanation,
              contentElementId: contentElement.id,
              entryIndex,
            })
          }
        />
      );
    default:
      return <StoryboardFallback title={contentElement.title} />;
  }
};
